import React, { useState } from "react";
import { Send, MessageCircle, Check, Ship } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { createRequest, buildWhatsAppLink } from "../lib/api";
import { interp } from "../lib/destinations";
import { toast } from "sonner";

export const YachtForm = ({ destinationName }) => {
  const { t } = useI18n();
  const [done, setDone] = useState(false);
  const [waLink, setWaLink] = useState("#");
  const [submitting, setSubmitting] = useState(false);
  const [f, setF] = useState({
    destination: destinationName || "", checkin: "", checkout: "", guests: "",
    size: "", budget: "", name: "", phone: "", email: "", notes: "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const inputCls = "w-full bg-transparent border border-vm-border text-vm-text placeholder:text-vm-muted px-4 py-3 text-sm focus:border-vm-accent transition-colors duration-300 rounded-none";

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.destination) { toast.error(t("request.required")); return; }
    setSubmitting(true);
    const message =
      `Yacht charter — ${f.destination}. ${f.checkin || "?"} – ${f.checkout || "?"}. ` +
      `${f.guests || "?"} guests. Size: ${f.size || "-"}. Budget: ${f.budget || "-"}. ${f.notes || ""}`;
    const payload = {
      name: f.name, email: f.email, phone: f.phone, contact_method: "whatsapp",
      category: "yacht", urgency: "whenever", location: f.destination, date: f.checkin,
      message, source: "yacht_form",
      details: { destination: f.destination, checkin: f.checkin, checkout: f.checkout, guests: f.guests, size: f.size, budget: f.budget },
    };
    try {
      await createRequest(payload);
      setWaLink(buildWhatsAppLink({ ...payload, category: t("request.categories.yacht"), urgency: t("request.urgencies.whenever") }));
      setDone(true);
      toast.success(t("request.success"));
    } catch { toast.error("Error. Please try WhatsApp."); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="border border-vm-border p-10 text-center" data-testid="yacht-form-success">
        <div className="mx-auto mb-6 w-14 h-14 border border-vm-accent flex items-center justify-center"><Check strokeWidth={1} size={26} className="text-vm-accent" /></div>
        <h3 className="font-serif text-3xl font-light mb-3">{t("request.success")}</h3>
        <p className="text-vm-text2 text-sm mb-8">{t("common.availability")}</p>
        <a href={waLink} target="_blank" rel="noopener noreferrer" className="vm-btn-primary inline-flex items-center gap-2 px-8 py-3 text-sm tracking-wide" data-testid="yacht-continue-whatsapp"><MessageCircle strokeWidth={1.5} size={18} /> {t("request.openWhatsApp")}</a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-vm-border p-8 md:p-12" data-testid="yacht-form">
      <div className="flex items-center gap-3 mb-2"><Ship strokeWidth={1.25} size={18} className="text-vm-accent" /><p className="vm-label">{t("yachts.label")}</p></div>
      <h3 className="font-serif text-3xl md:text-4xl font-light mb-2">{interp(t("yachtForm.title"), destinationName)}</h3>
      <p className="text-vm-text2 text-sm mb-8">{t("yachtForm.proposal")}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <input value={f.destination} onChange={(e) => set("destination", e.target.value)} placeholder={t("request.category")} className={inputCls} data-testid="yacht-destination" />
        <input type="number" min="1" value={f.guests} onChange={(e) => set("guests", e.target.value)} placeholder={t("common.guests")} className={inputCls} data-testid="yacht-guests" />
        <input type="date" value={f.checkin} onChange={(e) => set("checkin", e.target.value)} className={inputCls} data-testid="yacht-checkin" aria-label={t("yachtForm.checkin")} />
        <input type="date" value={f.checkout} onChange={(e) => set("checkout", e.target.value)} className={inputCls} data-testid="yacht-checkout" aria-label={t("yachtForm.checkout")} />
        <input value={f.size} onChange={(e) => set("size", e.target.value)} placeholder={t("yachtForm.size")} className={inputCls} data-testid="yacht-size" />
        <input value={f.budget} onChange={(e) => set("budget", e.target.value)} placeholder={t("common.budget")} className={inputCls} data-testid="yacht-budget" />
        <input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder={t("common.name")} className={inputCls} data-testid="yacht-name" />
        <input value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder={t("common.phone")} className={inputCls} data-testid="yacht-phone" />
        <input value={f.email} onChange={(e) => set("email", e.target.value)} placeholder={t("common.emailLabel")} className={inputCls} data-testid="yacht-email" />
      </div>
      <textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder={t("common.requirements")} rows={3} className={inputCls + " resize-none mb-6"} data-testid="yacht-notes" />
      <button type="submit" disabled={submitting} className="vm-btn-primary w-full inline-flex items-center justify-center gap-2 py-4 text-xs tracking-widest uppercase disabled:opacity-50" data-testid="yacht-submit"><Send strokeWidth={1.5} size={16} /> {submitting ? t("request.sending") : t("yachtForm.submit")}</button>
    </form>
  );
};
